/**
 * Engineer impersonation teardown — web platform (spec 048, T093).
 *
 * Single source of truth for taking the frontend out of impersonation
 * mode. Store state is injected via a getter closure to avoid a Zustand
 * dependency in the SDK package.
 *
 * App-specific concerns (toast, logger) are injected as callback props on
 * `SdkImpersonationProvider`, which passes them to `teardownImpersonation`.
 *
 * Sequence:
 *   1. Abort all in-flight impersonated requests.
 *   2. Call `onSetInactive(reason)` to reset the store.
 *   3. Call `onExpiredToast()` only for the `expired` reason.
 *   4. Attempt backend revocation in the background.
 *
 * Idempotent — no-op when `getState().isActive` is false.
 *
 * Relocated from `frontend/src/lib/impersonation/teardown.ts` as part of
 * the auth cutover (spec 009, FILE 14, Group F).
 */

import type { TeardownReason } from '../../../../auth/impersonation/types.js';
import { GrantApiClientError, revokeGrant } from './impersonation-api-client.js';

export interface TeardownState {
  isActive: boolean;
  grantToken: string | null;
  network: string | null;
  abortController: AbortController | null;
}

export interface TeardownCallbacks {
  /** Called to reset store state and wipe sessionStorage. */
  onSetInactive: (reason: TeardownReason) => void;
  /** Called when the session expired involuntarily (show a toast). */
  onExpiredToast?: () => void;
  /** Called for unexpected revoke errors (to log them). */
  onRevokeError?: (message: string, reason: TeardownReason) => void;
}

/**
 * Tear down the engineer-impersonation session.
 *
 * @param state - Snapshot of current impersonation state.
 * @param callbacks - App-level side-effect callbacks.
 */
export function teardownImpersonation(
  state: TeardownState,
  callbacks: TeardownCallbacks,
  reason: TeardownReason = 'engineer_stop',
): Promise<void> {
  if (!state.isActive || state.grantToken === null || state.network === null) {
    return Promise.resolve();
  }

  const tokenAtStart = state.grantToken;

  // 1. Local cleanup must never wait for the network. The old order awaited
  // revoke first, so a hung connection left the engineer in the target view.
  state.abortController?.abort();
  callbacks.onSetInactive(reason);
  if (reason === 'expired') {
    callbacks.onExpiredToast?.();
  }

  // 2. The backend still gets a revoke attempt, but its failure or timeout
  // cannot prevent the engineer identity from being restored locally.
  void revokeGrant(tokenAtStart).catch((err: unknown): void => {
    if (
      err instanceof GrantApiClientError &&
      (err.code === 'IMPERSONATION_GRANT_INVALID' || err.code === 'IMPERSONATION_GRANT_EXPIRED')
    ) {
      return;
    }
    callbacks.onRevokeError?.(err instanceof Error ? err.message.slice(0, 200) : 'unknown', reason);
  });

  return Promise.resolve();
}
